import { useEffect, useState } from "react";
import { useTable } from "@refinedev/antd";
import {
  Card, Table, Tag, Select, Space, Button, Modal, Input, Badge,
  Typography, Row, Col, Statistic, Divider, Collapse, Result, Alert, Empty,
} from "antd";
import {
  SyncOutlined, ExperimentOutlined, SearchOutlined,
  CheckCircleOutlined, WarningOutlined, CloseCircleOutlined,
  InfoCircleOutlined, ExclamationCircleOutlined, QuestionCircleOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import type { CrudFilters } from "@refinedev/core";
import { usePageTitle } from "../../../hooks";
import { apiClient } from "../../../services/api/apiClient";

export interface SampleRecord {
  id: number;
  sample_reference: string;
  sample_type: "leaf" | "skin";
  lab_code: string | null;
  audit: number;
  audit_reference: string;
  audit_plot: number | null;
  audit_plot_name: string | null;
  control_point_review: number | null;
  control_point_review_name: string | null;
  latest_result: string | null;
  // Denormalizado desde `consensus.normalized_result` de Lab Plates
  // (más fiable que `latest_result` cuando hay varias placas).
  consensus_state: string | null;
  consensus_variety: string | null;
  last_lab_sync_at: string | null;
  // Metadatos de la auditoría: productor + finca + campaña (año).
  // Expuestos por SampleAdminSerializer para facilitar la búsqueda y
  // filtrado del listado admin sin abrir el detalle de la auditoría.
  producer_name: string | null;
  farm_name: string | null;
  audit_start_date: string | null;
  season_year: number | null;
  season_id: number | null;
  created: string;
  modified: string;
}

interface SeasonOption {
  id: number;
  year: number;
}

interface SyncReport {
  queried: number;
  updated: number;
  results_created: number;
  not_found: number;
  invalid_codes: number;
  errors: number;
  anomalies: Array<{
    sample_id: string;
    sample_reference: string;
    lab_code: string;
    audit_reference: string;
    audit_id: number;
    consensus_state: string;
    anomaly_type: "CP_TANGO" | "PLOT_NO_TANGO";
    producer_name?: string;
    season_year?: string;
    farm_name?: string;
  }>;
  not_found_samples?: Array<{
    sample_id: string;
    sample_reference: string;
    lab_code: string;
    audit_reference: string;
    audit_id: number;
  }>;
  invalid_codes_list?: Array<{
    sample_id: string;
    lab_code: string;
  }>;
}

// Estados que Lab Plates puede devolver, mapeados al modelo local.
// "NO_AMPLIFICA" y "NO_APLICA" quedan como legacy — muestran datos
// históricos previos a la integración con Lab Plates.
const RESULT_STATE_OPTIONS = [
  { label: "Pendiente", value: "PENDIENTE" },
  { label: "Tango", value: "TANGO" },
  { label: "No Tango", value: "NO_TANGO" },
  { label: "No Concluyente", value: "NC" },
  { label: "No Amplificada", value: "NA" },
  { label: "Divergente (revisar)", value: "MIXED" },
  { label: "Otra variedad", value: "OTHER_VARIETY" },
  { label: "No Amplifica (legacy)", value: "NO_AMPLIFICA" },
  { label: "No Aplica (legacy)", value: "NO_APLICA" },
];

const HAS_LAB_CODE_OPTIONS = [
  { label: "Con código", value: "true" },
  { label: "Sin código", value: "false" },
];

const getResultColor = (state: string | null): string => {
  switch (state) {
    case "TANGO":
      return "red";           // El productor tiene Tango → confirmar licencia
    case "NO_TANGO":
      return "green";         // Descartado
    case "PENDIENTE":
      return "orange";
    case "NC":
      return "gold";          // No concluyente — pendiente de repetir
    case "NA":
      return "default";       // No analizada
    case "MIXED":
      return "magenta";       // Divergente — revisión manual
    case "OTHER_VARIETY":
      return "geekblue";      // Otra variedad detectada
    case "NO_AMPLIFICA":
      return "purple";
    case "NO_APLICA":
      return "default";
    default:
      return "default";
  }
};

const getResultLabel = (state: string | null): string => {
  switch (state) {
    case "TANGO":
      return "Tango";
    case "NO_TANGO":
      return "No Tango";
    case "PENDIENTE":
      return "Pendiente";
    case "NC":
      return "No Concluyente";
    case "NA":
      return "No Amplificada";
    case "MIXED":
      return "Divergente";
    case "OTHER_VARIETY":
      return "Otra variedad";
    case "NO_AMPLIFICA":
      return "No Amplifica";
    case "NO_APLICA":
      return "No Aplica";
    default:
      return "Sin resultado";
  }
};

export const SampleAdminList = () => {
  const { t } = useTranslation();
  usePageTitle(t("samples.admin_title", "Muestras de Laboratorio"));

  const [resultStateFilter, setResultStateFilter] = useState<string | undefined>();
  const [hasLabCodeFilter, setHasLabCodeFilter] = useState<string | undefined>();
  const [seasonFilter, setSeasonFilter] = useState<number | undefined>();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncReport, setSyncReport] = useState<SyncReport | null>(null);
  const [editingLabCode, setEditingLabCode] = useState<number | null>(null);
  const [labCodeValue, setLabCodeValue] = useState("");
  const [seasons, setSeasons] = useState<SeasonOption[]>([]);

  // Cargamos las temporadas una sola vez al montar. Es un catálogo
  // cerrado y pequeño (~20 filas) — no vale la pena paginar.
  useEffect(() => {
    apiClient
      .get<{ data?: SeasonOption[]; results?: SeasonOption[] }>(
        "/v1/seasons/?page_size=200",
      )
      .then((res) => {
        const raw =
          (res.data as any)?.data
          ?? (res.data as any)?.results
          ?? (Array.isArray(res.data) ? res.data : []);
        // De-dup por año y ordenar descendente (campañas recientes
        // primero — es lo que el admin filtra 99% del tiempo).
        const byYear = new Map<number, SeasonOption>();
        (raw as any[]).forEach((s) => {
          if (typeof s.year === "number" && !byYear.has(s.year)) {
            byYear.set(s.year, { id: s.id, year: s.year });
          }
        });
        setSeasons(Array.from(byYear.values()).sort((a, b) => b.year - a.year));
      })
      .catch(() => setSeasons([]));
  }, []);

  const { tableProps, setFilters } = useTable<SampleRecord>({
    resource: "v1/samples",
    sorters: {
      // Orden por defecto: fecha de la auditoría descendente (audits
      // más recientes primero). Antes ordenaba por `created` (fecha
      // de creación de la muestra) que no siempre refleja la fecha
      // real del muestreo.
      initial: [{ field: "audit__start_date", order: "desc" }],
    },
    pagination: { pageSize: 15 },
    syncWithLocation: true,
  });

  // Empuja los filtros actuales al Refine table. Los campos custom
  // (`season_year`, `q`) pasan por el data provider tal cual → los
  // recoge el sample_viewset como query params.
  const applyFilters = (patch: Partial<{
    result_state: string | undefined;
    has_lab_code: string | undefined;
    season_year: number | undefined;
    q: string;
  }>) => {
    const nextResultState =
      "result_state" in patch ? patch.result_state : resultStateFilter;
    const nextHasLabCode =
      "has_lab_code" in patch ? patch.has_lab_code : hasLabCodeFilter;
    const nextSeason =
      "season_year" in patch ? patch.season_year : seasonFilter;
    const nextQ =
      "q" in patch ? (patch.q ?? "") : searchQuery;

    if ("result_state" in patch) setResultStateFilter(patch.result_state);
    if ("has_lab_code" in patch) setHasLabCodeFilter(patch.has_lab_code);
    if ("season_year" in patch) setSeasonFilter(patch.season_year);
    if ("q" in patch) setSearchQuery(patch.q ?? "");

    const newFilters: CrudFilters = [];
    if (nextResultState) {
      newFilters.push({ field: "result_state", operator: "eq", value: nextResultState });
    }
    if (nextHasLabCode) {
      newFilters.push({ field: "has_lab_code", operator: "eq", value: nextHasLabCode });
    }
    if (nextSeason) {
      newFilters.push({ field: "season_year", operator: "eq", value: nextSeason });
    }
    if (nextQ.trim()) {
      newFilters.push({ field: "q", operator: "eq", value: nextQ.trim() });
    }
    setFilters(newFilters, "replace");
  };

  const handleClearFilters = () => {
    setResultStateFilter(undefined);
    setHasLabCodeFilter(undefined);
    setSeasonFilter(undefined);
    setSearchQuery("");
    setFilters([], "replace");
  };

  const handleSyncResults = async () => {
    setSyncLoading(true);
    try {
      // Timeout extendido a 5 min: el sync procesa cientos de muestras
      // en una sola request síncrona (batches de 200 contra Lab Plates
      // + persistencia + reconciliación). El default de axios (0/none o
      // 30s según config) provocaba HTTP 499 en el navegador antes de
      // que el server terminara. Refactorizarlo a Celery task async
      // sería lo canónico, pero hasta entonces subimos el tolerancia.
      const { data } = await apiClient.post<SyncReport>(
        "/v1/samples/sync_results/",
        undefined,
        { timeout: 5 * 60 * 1000 },
      );
      setSyncReport(data);
      setSyncModalVisible(true);
      // Refresh table by re-applying current filters
      setFilters([], "replace");
    } catch {
      setSyncReport({
        queried: 0, updated: 0, results_created: 0, not_found: 0,
        invalid_codes: 0, errors: -1, anomalies: [],
        not_found_samples: [], invalid_codes_list: [],
      });
      setSyncModalVisible(true);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleAssignLabCode = async (sampleId: number) => {
    if (!labCodeValue.trim()) return;
    try {
      await apiClient.patch(`/v1/samples/${sampleId}/assign_lab_code/`, {
        lab_code: labCodeValue.trim(),
      });
      setEditingLabCode(null);
      setLabCodeValue("");
      // Refresh table
      setFilters([], "replace");
    } catch {
      // Error handled by apiClient interceptor
    }
  };

  const getOriginName = (record: SampleRecord): string => {
    return record.audit_plot_name || record.control_point_review_name || "-";
  };

  return (
    <Card>
      <PageHeader
        icon={<ExperimentOutlined />}
        accent="#EB2F96"
        title={t("samples.admin_title", "Muestras de Laboratorio")}
        subtitle={t(
          "samples.admin_subtitle",
          "Listado de muestras enviadas a laboratorio y sus resultados",
        )}
        extra={
          <Button
            type="primary"
            icon={<SyncOutlined />}
            loading={syncLoading}
            onClick={handleSyncResults}
          >
            {t("samples.sync_pending", "Sincronizar resultados pendientes")}
          </Button>
        }
      />
      {/* Filters */}
      <Space style={{ marginBottom: 16, width: "100%" }} wrap>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t(
            "samples.search_placeholder",
            "Buscar por código muestra/laboratorio, referencia auditoría, productor, finca o waypoint",
          )}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onPressEnter={() => applyFilters({ q: searchQuery })}
          onBlur={() => applyFilters({ q: searchQuery })}
          style={{ width: 360 }}
          data-testid="search-input"
        />
        <Select
          placeholder={t("samples.filter_season", "Campaña")}
          allowClear
          style={{ width: 140 }}
          value={seasonFilter}
          onChange={(value) => applyFilters({ season_year: value })}
          options={seasons.map((s) => ({ label: `${s.year}`, value: s.year }))}
          data-testid="season-filter"
          showSearch
          optionFilterProp="label"
        />
        <Select
          placeholder={t("samples.filter_result_state", "Estado resultado")}
          allowClear
          style={{ width: 200 }}
          value={resultStateFilter}
          onChange={(value) => applyFilters({ result_state: value })}
          options={RESULT_STATE_OPTIONS}
          data-testid="result-state-filter"
        />
        <Select
          placeholder={t("samples.filter_has_lab_code", "Código laboratorio")}
          allowClear
          style={{ width: 180 }}
          value={hasLabCodeFilter}
          onChange={(value) => applyFilters({ has_lab_code: value })}
          options={HAS_LAB_CODE_OPTIONS}
          data-testid="has-lab-code-filter"
        />
        <Button onClick={handleClearFilters}>
          {t("common.clear_filters", "Limpiar filtros")}
        </Button>
      </Space>

      {/* Table */}
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
        <Table.Column
          dataIndex="lab_code"
          title={t("samples.lab_code", "Código Lab.")}
          width={180}
          fixed="left"
          render={(value: string | null, record: SampleRecord) => {
            if (editingLabCode === record.id) {
              return (
                <Space>
                  <Input
                    size="small"
                    value={labCodeValue}
                    onChange={(e) => setLabCodeValue(e.target.value)}
                    onPressEnter={() => handleAssignLabCode(record.id)}
                    placeholder="LAB-XXXXX"
                    style={{ width: 120 }}
                    data-testid="lab-code-input"
                  />
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => handleAssignLabCode(record.id)}
                  >
                    OK
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setEditingLabCode(null);
                      setLabCodeValue("");
                    }}
                  >
                    ✕
                  </Button>
                </Space>
              );
            }
            return value ? (
              <span>{value}</span>
            ) : (
              <Button
                size="small"
                type="link"
                onClick={() => {
                  setEditingLabCode(record.id);
                  setLabCodeValue("");
                }}
              >
                {t("samples.assign_code", "Asignar")}
              </Button>
            );
          }}
        />
        <Table.Column
          dataIndex="sample_type"
          title={t("samples.type", "Tipo")}
          width={100}
          render={(value: string) => (
            <Tag icon={<ExperimentOutlined />} color={value === "leaf" ? "green" : "orange"}>
              {value === "leaf"
                ? t("samples.type_leaf", "Hoja")
                : t("samples.type_skin", "Piel")}
            </Tag>
          )}
        />
        <Table.Column
          title={t("samples.origin", "Origen")}
          width={220}
          render={(_: unknown, record: SampleRecord) => {
            // Distinguir visualmente cuando la muestra viene de un
            // punto de control (material sospechoso) vs de una
            // parcela auditada. El tag rojo salta a la vista y evita
            // que el admin tenga que saber los nombres para inferirlo.
            const isControlPoint = record.control_point_review != null;
            return (
              <Space size={4} direction="vertical" style={{ alignItems: "flex-start" }}>
                <span>{getOriginName(record)}</span>
                {isControlPoint ? (
                  <Tag color="red" style={{ marginRight: 0 }}>
                    {t("samples.origin_cp", "Punto de control")}
                  </Tag>
                ) : (
                  <Tag color="green" style={{ marginRight: 0 }}>
                    {t("samples.origin_plot", "Parcela")}
                  </Tag>
                )}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="producer_name"
          title={t("samples.producer", "Productor")}
          width={200}
          ellipsis
          render={(value: string | null, record: SampleRecord) => (
            <Space direction="vertical" size={0}>
              <span>{value || "—"}</span>
              {record.farm_name ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {record.farm_name}
                </Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="season_year"
          title={t("samples.season", "Campaña")}
          width={100}
          render={(value: number | null) =>
            value != null ? (
              <Tag color="geekblue" style={{ marginRight: 0 }}>{value}</Tag>
            ) : (
              <Typography.Text type="secondary">—</Typography.Text>
            )
          }
        />
        <Table.Column
          dataIndex="audit_start_date"
          title={t("samples.audit_start_date", "Fecha auditoría")}
          width={130}
          sorter
          key="audit__start_date"
          render={(value: string | null) =>
            value ? (
              <span>
                {new Date(value).toLocaleDateString("es-ES", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </span>
            ) : (
              <Typography.Text type="secondary">—</Typography.Text>
            )
          }
        />
        <Table.Column
          dataIndex="audit_reference"
          title={t("samples.audit", "Auditoría")}
          width={150}
          render={(value: string, record: SampleRecord) => (
            // Enlace a la auditoría en nueva pestaña — el admin
            // suele revisar varias muestras seguidas, y abrir cada
            // detalle en el mismo tab implicaría perder el filtrado
            // del listado. Con `target=_blank` el listado queda
            // intacto y las auditorías se abren en tabs paralelas.
            <a
              href={`/audits/${record.audit}`}
              target="_blank"
              rel="noopener noreferrer"
              title={t("samples.audit_link_hint", "Abrir auditoría en nueva pestaña")}
            >
              {value || `#${record.audit}`}
            </a>
          )}
        />
        <Table.Column
          dataIndex="consensus_state"
          title={t("samples.consensus_state", "Consenso")}
          width={170}
          render={(value: string | null, record: SampleRecord) => (
            <Space direction="vertical" size={0}>
              <Badge color={getResultColor(value)} text={getResultLabel(value)} />
              {value === "OTHER_VARIETY" && record.consensus_variety ? (
                <span style={{ fontSize: 12, color: "#8b8b8b" }}>
                  → {record.consensus_variety}
                </span>
              ) : null}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="latest_result"
          title={t("samples.latest_plate", "Última placa")}
          width={140}
          render={(value: string | null) => (
            <Badge
              color={getResultColor(value)}
              text={getResultLabel(value)}
            />
          )}
        />
      </Table>

      {/* Sync Summary Modal — visual con Statistics + Collapse de detalles.
          El modal aparece justo tras la respuesta del endpoint
          `/v1/samples/sync_results/` y resume qué se hizo. */}
      <Modal
        title={
          <Space>
            <SyncOutlined spin={syncLoading} style={{ color: "#1677ff" }} />
            <span>
              {t("samples.sync_summary", "Resumen de sincronización")}
            </span>
          </Space>
        }
        open={syncModalVisible}
        onOk={() => setSyncModalVisible(false)}
        onCancel={() => setSyncModalVisible(false)}
        cancelButtonProps={{ style: { display: "none" } }}
        okText={t("common.close", "Cerrar")}
        width={780}
      >
        {/* Error de red (errors=-1 marca fallo previo al server) */}
        {syncReport && syncReport.errors === -1 ? (
          <Result
            status="error"
            title={t("samples.sync_error_title", "No se pudo sincronizar")}
            subTitle={t(
              "samples.sync_error_subtitle",
              "El laboratorio no respondió o la petición se cortó. Reintenta en unos minutos.",
            )}
          />
        ) : syncReport ? (
          <>
            {/* Banner de estado global — verde/naranja/rojo según report */}
            {syncReport.errors > 0 ? (
              <Alert
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                message={t(
                  "samples.sync_partial",
                  "Sincronización terminada con {{n}} errores",
                  { n: syncReport.errors },
                )}
                description={t(
                  "samples.sync_partial_hint",
                  "Los cambios aplicados se guardaron; revisa el detalle abajo.",
                )}
                style={{ marginBottom: 16 }}
              />
            ) : syncReport.updated > 0 || syncReport.results_created > 0 ? (
              <Alert
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                message={t(
                  "samples.sync_success",
                  "Sincronización completada correctamente",
                )}
                style={{ marginBottom: 16 }}
              />
            ) : (
              <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                message={t(
                  "samples.sync_noop",
                  "Sin cambios — no había resultados nuevos en el laboratorio",
                )}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Statistics principales */}
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Card size="small" bordered>
                  <Statistic
                    title={t("samples.sync_queried", "Consultadas")}
                    value={syncReport.queried}
                    prefix={<SyncOutlined style={{ color: "#1677ff" }} />}
                    valueStyle={{ color: "#1677ff" }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" bordered>
                  <Statistic
                    title={t("samples.sync_updated", "Actualizadas")}
                    value={syncReport.updated}
                    prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" bordered>
                  <Statistic
                    title={t("samples.sync_results_created_short", "Placas nuevas")}
                    value={syncReport.results_created ?? 0}
                    prefix={<ExperimentOutlined style={{ color: "#722ed1" }} />}
                    valueStyle={{ color: "#722ed1" }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" bordered>
                  <Statistic
                    title={t("samples.sync_not_found_short", "No encontradas")}
                    value={syncReport.not_found ?? 0}
                    prefix={
                      <QuestionCircleOutlined style={{ color: "#8c8c8c" }} />
                    }
                    valueStyle={{
                      color: (syncReport.not_found ?? 0) > 0 ? "#faad14" : "#8c8c8c",
                    }}
                  />
                </Card>
              </Col>
              {syncReport.invalid_codes > 0 ? (
                <Col xs={12} sm={6}>
                  <Card size="small" bordered>
                    <Statistic
                      title={t("samples.sync_invalid_short", "Códigos inválidos")}
                      value={syncReport.invalid_codes}
                      prefix={
                        <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                      }
                      valueStyle={{ color: "#faad14" }}
                    />
                  </Card>
                </Col>
              ) : null}
              {syncReport.errors > 0 ? (
                <Col xs={12} sm={6}>
                  <Card size="small" bordered>
                    <Statistic
                      title={t("samples.sync_errors_short", "Errores")}
                      value={syncReport.errors}
                      prefix={<CloseCircleOutlined style={{ color: "#f5222d" }} />}
                      valueStyle={{ color: "#f5222d" }}
                    />
                  </Card>
                </Col>
              ) : null}
            </Row>

            {/* Anomalías — el KPI más importante, arriba del todo si hay */}
            {syncReport.anomalies && syncReport.anomalies.length > 0 ? (
              <>
                <Divider orientation="left" style={{ marginTop: 24 }}>
                  <Space>
                    <WarningOutlined style={{ color: "#c0392b" }} />
                    <Typography.Text strong style={{ color: "#c0392b" }}>
                      {t("samples.sync_anomalies", "Anomalías detectadas")}
                      {" "}({syncReport.anomalies.length})
                    </Typography.Text>
                  </Space>
                </Divider>
                <div style={{ maxHeight: 260, overflowY: "auto" }}>
                  {syncReport.anomalies.map((a) => {
                    const context = [
                      a.producer_name,
                      a.farm_name ? `finca ${a.farm_name}` : "",
                      a.season_year ? `campaña ${a.season_year}` : "",
                    ]
                      .filter(Boolean)
                      .join(" · ");
                    const isCpTango = a.anomaly_type === "CP_TANGO";
                    return (
                      <Card
                        key={a.sample_id}
                        size="small"
                        style={{ marginBottom: 8, background: isCpTango ? "#fff1f0" : "#fff7e6" }}
                      >
                        <Space direction="vertical" size={2} style={{ width: "100%" }}>
                          <Space>
                            <Tag color={isCpTango ? "red" : "orange"}>
                              {isCpTango
                                ? t("samples.anomaly_cp_tango", "PC → TANGO")
                                : t("samples.anomaly_plot_no_tango", "Parcela → NO TANGO")}
                            </Tag>
                            <code>{a.lab_code || "sin código"}</code>
                            <span>·</span>
                            <a
                              href={`/audits/${a.audit_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {a.audit_reference || `#${a.audit_id}`}
                            </a>
                          </Space>
                          {context ? (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              ↳ {context}
                            </Typography.Text>
                          ) : null}
                        </Space>
                      </Card>
                    );
                  })}
                </div>
              </>
            ) : null}

            {/* Listas secundarias en Collapse — no ensucian la vista */}
            {(syncReport.not_found_samples?.length ?? 0) > 0 ||
             (syncReport.invalid_codes_list?.length ?? 0) > 0 ? (
              <Collapse
                ghost
                style={{ marginTop: 16 }}
                items={[
                  ...(syncReport.not_found_samples && syncReport.not_found_samples.length > 0
                    ? [{
                        key: "not_found",
                        label: (
                          <Space>
                            <QuestionCircleOutlined style={{ color: "#8c8c8c" }} />
                            <Typography.Text>
                              {t("samples.sync_not_found_list", "Muestras no encontradas")}
                              {" "}({syncReport.not_found_samples.length})
                            </Typography.Text>
                          </Space>
                        ),
                        children: (
                          <>
                            <Typography.Paragraph
                              type="secondary"
                              style={{ fontSize: 12, marginBottom: 8 }}
                            >
                              {t(
                                "samples.sync_not_found_hint",
                                "Revisa si el código está mal escrito o si la placa aún no ha llegado al laboratorio.",
                              )}
                            </Typography.Paragraph>
                            <div style={{ maxHeight: 200, overflowY: "auto" }}>
                              {syncReport.not_found_samples.map((nf) => (
                                <div key={nf.sample_id} style={{ marginBottom: 4 }}>
                                  <code>{nf.lab_code}</code>
                                  {" · "}
                                  <a
                                    href={`/audits/${nf.audit_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {nf.audit_reference || `#${nf.audit_id}`}
                                  </a>
                                </div>
                              ))}
                            </div>
                          </>
                        ),
                      }]
                    : []),
                  ...(syncReport.invalid_codes_list && syncReport.invalid_codes_list.length > 0
                    ? [{
                        key: "invalid",
                        label: (
                          <Space>
                            <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                            <Typography.Text>
                              {t("samples.sync_invalid_list", "Códigos inválidos (no enviados)")}
                              {" "}({syncReport.invalid_codes_list.length})
                            </Typography.Text>
                          </Space>
                        ),
                        children: (
                          <>
                            <Typography.Paragraph
                              type="secondary"
                              style={{ fontSize: 12, marginBottom: 8 }}
                            >
                              {t(
                                "samples.sync_invalid_codes_hint",
                                "Deben cumplir 5 caracteres A-Z/0-9. Corrige el código de laboratorio manualmente.",
                              )}
                            </Typography.Paragraph>
                            <div style={{ maxHeight: 200, overflowY: "auto" }}>
                              {syncReport.invalid_codes_list.map((ic) => (
                                <div key={ic.sample_id} style={{ marginBottom: 4 }}>
                                  <code>{ic.lab_code}</code>
                                  {" · sample "}
                                  <code style={{ fontSize: 11 }}>{ic.sample_id}</code>
                                </div>
                              ))}
                            </div>
                          </>
                        ),
                      }]
                    : []),
                ]}
              />
            ) : null}
          </>
        ) : (
          <Empty description={t("samples.sync_no_data", "Sin datos de sincronización")} />
        )}
      </Modal>
    </Card>
  );
};
